import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { messageDeliveriesService } from "@/services/message-deliveries-service";
import {
  MessageDelivery,
  MessageDeliveryFilters,
  MESSAGE_DELIVERIES_PAGE_SIZE,
} from "@/types/message-delivery";

export const messageDeliveryKeys = {
  all: ["messageDeliveries"] as const,
  list: (hotelId: string, filters: MessageDeliveryFilters) =>
    [...messageDeliveryKeys.all, "list", hotelId, filters] as const,
};

export const useMessageDeliveries = (
  hotelId: string | null,
  filters: MessageDeliveryFilters,
) => {
  const query = useInfiniteQuery<MessageDelivery[]>({
    queryKey: messageDeliveryKeys.list(hotelId ?? "", filters),
    enabled: !!hotelId,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      messageDeliveriesService.list(hotelId as string, {
        ...filters,
        limit: MESSAGE_DELIVERIES_PAGE_SIZE,
        cursor: pageParam as string | undefined,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.length < MESSAGE_DELIVERIES_PAGE_SIZE
        ? undefined
        : lastPage[lastPage.length - 1].id,
    // Diagnostic audit log — no aggressive caching, manual refresh only.
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const rows = useMemo(
    () => (query.data?.pages ?? []).flat(),
    [query.data?.pages],
  );

  return {
    rows,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
